import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface QuoteData {
  id: string;
  title: string;
  description: string;
  items: QuoteItem[];
  totalAmount: number;
  validUntil: string;
  serviceType: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  created: string;
  updated: string;
}

export interface QuoteItem {
  id: string;
  description: string;
  amount: number;
  quantity?: number;
  unit?: string;
}

export interface ACHData {
  id: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  accountHolderName: string;
  billingAddress: string;
  city: string;
  state: string;
  zipCode: string;
  status: 'active' | 'inactive' | 'pending';
  created: string;
  updated: string;
}

export interface CustomerEngagementLetter {
  id: string;
  red_rock_id: string;
  engagement_letter_link?: string;
  active: boolean;
  created: string;
  updated: string;
}

class EngagementService {
  // Quote Management
  async getPendingQuotes(clientId: string, redRockId: string): Promise<QuoteData[]> {
    try {
      // This would typically call a backend endpoint to get pending quotes
      // For now, return mock data
      return [
        {
          id: 'quote_001',
          title: '2024 Tax Return Filing',
          description: 'Complete tax return preparation and filing for 2024 tax year',
          items: [
            {
              id: 'item_001',
              description: 'Tax Return Preparation',
              amount: 50000, // $500.00 in cents
              quantity: 1,
              unit: 'return'
            },
            {
              id: 'item_002',
              description: 'Electronic Filing',
              amount: 2000, // $20.00 in cents
              quantity: 1,
              unit: 'filing'
            }
          ],
          totalAmount: 52000, // $520.00 in cents
          validUntil: '2024-12-31',
          serviceType: 'tax_filing',
          status: 'pending',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        }
      ];
    } catch (error: any) {
      console.error('Failed to fetch pending quotes:', error);
      return [];
    }
  }

  async approveQuote(quoteId: string, clientId: string, redRockId: string): Promise<boolean> {
    try {
      // This would typically call a backend endpoint to approve the quote
      console.log(`Quote ${quoteId} approved for client ${clientId}, customer ${redRockId}`);
      return true;
    } catch (error: any) {
      console.error('Failed to approve quote:', error);
      return false;
    }
  }

  // ACH Management
  async getACHInfo(clientId: string, redRockId: string): Promise<ACHData | null> {
    // This would typically call a backend endpoint to get ACH information
    // For now, return null (no ACH info set up)
    return null;
  }

  async saveACHInfo(achData: Omit<ACHData, 'id' | 'created' | 'updated'>, clientId: string, redRockId: string): Promise<ACHData> {
    try {
      // This would typically call a backend endpoint to save ACH information
      const newACHData: ACHData = {
        ...achData,
        id: `ach_${Date.now()}`,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      
      console.log(`ACH info saved for client ${clientId}, customer ${redRockId}`);
      return newACHData;
    } catch (error: any) {
      console.error('Failed to save ACH information:', error);
      throw new Error('Failed to save ACH information');
    }
  }

  // Engagement Letter Management
  async getActiveEngagementLetter(redRockId: string): Promise<CustomerEngagementLetter | null> {
    try {
      // Fetch engagement letter where red_rock_id matches and active is true
      // Use the same pattern as termsConditionsService and userManagementService
      // Double quotes for string fields, no quotes for boolean, with encodeURIComponent
      const filter = `red_rock_id="${redRockId}" && active=true`;
      const response = await api.get(
        `/collections/customer_engagement_letter/records?filter=${encodeURIComponent(filter)}`
      );
      const data = response.data;
      
      if (data.items && data.items.length > 0) {
        return data.items[0];
      }
      return null;
    } catch (error: any) {
      console.error('Failed to fetch engagement letter:', error);
      // Log the full error for debugging
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Request URL:', error.config?.url);
      }
      return null;
    }
  }

  // Service Status
  async getServiceStatus(clientId: string, redRockId: string): Promise<{
    hasActiveACH: boolean;
    hasPendingQuotes: boolean;
    serviceType: 'one_time' | 'recurring' | 'none';
  }> {
    try {
      const [achInfo, pendingQuotes] = await Promise.all([
        this.getACHInfo(clientId, redRockId),
        this.getPendingQuotes(clientId, redRockId)
      ]);

      return {
        hasActiveACH: achInfo?.status === 'active',
        hasPendingQuotes: pendingQuotes.length > 0,
        serviceType: achInfo?.status === 'active' ? 'recurring' : 
                    pendingQuotes.length > 0 ? 'one_time' : 'none'
      };
    } catch (error: any) {
      console.error('Failed to get service status:', error);
      return {
        hasActiveACH: false,
        hasPendingQuotes: false,
        serviceType: 'none'
      };
    }
  }
}

export const engagementService = new EngagementService();

